import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { useUnsavedWarning } from '@/hooks/useUnsavedWarning';
import { useWorkerAction } from '@/hooks/useWorkerAction';
import { updateOrganizationName } from '@/services/updateOrganizationName';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { SettingsSection } from '@/components/settings/SettingsSection';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

export function OrganizationSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const { orgId, org, online, canEdit } = useSettingsAccess();
  const { ensureReady } = useWorkerAction('settings-org-name');
  const [orgName, setOrgName] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setOrgName(org?.name ?? '');
    setDirty(false);
  }, [org?.name]);

  useUnsavedWarning(dirty);

  async function save() {
    if (!orgId || !online || !orgName.trim() || !canEdit) return;
    if (!(await ensureReady())) return;
    setSaving(true);
    try {
      await updateOrganizationName(orgId, orgName);
      toast.success(t('settings.orgNameSaved'), 'settings-org-name');
      setDirty(false);
    } catch (e) {
      toastError(toast, e, 'settings-org-name');
    } finally {
      setSaving(false);
    }
  }

  if (!org) return null;

  return (
    <SettingsSection id="settings-organization" title={t('settings.organization')}>
      {canEdit ? (
        <>
          <div>
            <Label htmlFor="org-name">{t('settings.orgName')}</Label>
            <Input
              id="org-name"
              className="mt-1 max-w-md"
              value={orgName}
              onChange={(e) => {
                setOrgName(e.target.value);
                setDirty(e.target.value.trim() !== (org.name ?? '').trim());
              }}
              disabled={!online}
            />
            <p className="mt-1 text-xs text-[var(--fv-text-muted)]">{t('settings.orgNameHint')}</p>
          </div>
          <Button
            variant="secondary"
            disabled={!online || saving || !orgName.trim() || !dirty}
            onClick={() => void save()}
          >
            {t('common.save')}
          </Button>
        </>
      ) : (
        <p className="text-sm">{org.name}</p>
      )}
    </SettingsSection>
  );
}
